import { DateFormat, type HostInfo, type TagColor } from '@equip.health/ui';
import { DateTime } from 'luxon';
import {
  type GroupTypeEnum,
  DAY_OF_WEEK_DISABLED_FREQUENCIES,
} from '../constants/groupClasses.constants';
import { GROUP_SECTION_MAX_ATTENDEES_LIMIT } from './classes/groupClasses.util';
import { getMaximumAttendeesErrorMessage } from './form.util';
import { groupClassHostToAutoCompleteOption } from './user.util';
import { roundToNext5Minutes, parseTimeString } from './date.util';
import { getNextOccurrenceDate } from '~/components/maud/util';
import { capitalizeFirstLetter } from '~/lib/common';
import type { TreatmentModalityEnumeration } from '../types/treatmentAttributes/treatmentAttributes';
import { formatAgeRangeDisplay } from './clinicalProfile.util';
import type { GetRegistrantsV2Response } from '~/store/services/groupClass';
import { ParticipantTypes } from '../constants/schedule.constants';
import { getPreferredFullName } from './patient.util';

interface FormatGroupSectionTimeSlotProps {
  start: string;
  duration: number;
  timeZone: string;
}
export interface GroupDetails {
  id: string;
  title: string;
  description: string;
  iconName: string;
  groupTypeCode?: GroupTypeEnum;
  groupTypeTagColor?: TagColor;
  groupStatusCode?: string;
  treatmentPrograms?: string[];
  treatmentModalities?: string[];
  diagnoses?: string[];
  ageRanges?: string[];
  attendeeRoles?: string[];
  sensitive?: boolean;
  core?: boolean;
}

interface GroupSectionRecurrenceProps {
  start: string;
  frequency: SectionFrequencyCode;
}

export const formatGroupSectionRecurrence = ({
  start,
  frequency,
}: GroupSectionRecurrenceProps): string | null => {
  const date = DateTime.fromISO(start);
  const dayOfWeek = date.isValid ? date.toFormat('cccc') : undefined;

  switch (frequency) {
    case 'DOES_NOT_REPEAT':
      return null;

    case 'EVERY_DAY':
      return 'Daily';

    case 'EVERY_WEEKDAY':
      return 'Every weekday';

    case 'WEEKLY':
      return dayOfWeek ? `Every ${dayOfWeek}` : 'Every week';

    case 'EVERY_OTHER_WEEK':
      return dayOfWeek ? `Every other ${dayOfWeek}` : 'Every other week';

    case 'EVERY_4_WEEKS':
      return dayOfWeek ? `Every 4 weeks on ${dayOfWeek}` : 'Every 4 weeks';

    case 'FIRST_OF_EVERY_MONTH':
      return 'First of every month';

    default:
      return '';
  }
};

export const formatGroupSectionTimeSlot = ({
  start,
  duration,
  timeZone,
}: FormatGroupSectionTimeSlotProps): string => {
  const startTime = DateTime.fromISO(start, { zone: timeZone });

  if (!startTime.isValid) {
    console.error('Invalid start time:', start, timeZone);
    return 'Invalid time';
  }

  if (duration <= 0) {
    console.error('Invalid duration:', duration);
    return 'Invalid duration';
  }

  const endTime = startTime.plus({ minutes: duration });

  const format = (dt: DateTime) =>
    dt
      .toFormat('h:mm a')
      .replace(':00', '')
      .replace('AM', 'am')
      .replace('PM', 'pm');

  return `${format(startTime)} - ${format(endTime)}`;
};

interface FormatGroupSectionDurationProps {
  startDate: string;
  endDate: string;
}

export const formatGroupSectionDuration = ({
  startDate,
  endDate,
}: FormatGroupSectionDurationProps): string => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  if (!start.isValid || !end.isValid) return null;

  const diffInWeeks = Math.round(end.diff(start, 'weeks').weeks);

  if (diffInWeeks <= 0) return null;

  return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'}`;
};
interface FormatGroupSectionDateRangeProps {
  start: string;
  end: string;
  timeZone: string;
}

export const formatGroupSectionDateRange = ({
  start,
  end,
  timeZone,
}: FormatGroupSectionDateRangeProps): string => {
  const startDate = DateTime.fromISO(start, { zone: timeZone });
  const endDate = DateTime.fromISO(end, { zone: timeZone });

  if (!startDate.isValid || !endDate.isValid) return '';

  const sameYear = startDate.year === endDate.year;

  const startFormat = sameYear ? 'MMM d' : 'MMM d, yyyy';
  const endFormat = 'MMM d, yyyy';

  return `${startDate.toFormat(startFormat)} - ${endDate.toFormat(endFormat)}`;
};

export const generateHostInitials = (host: {
  firstName?: string;
  lastName?: string;
  displayFirstName?: string;
  displayLastName?: string;
}): string => {
  const firstName =
    host.displayFirstName?.trim() || host.firstName?.trim() || '';
  const lastName = host.displayLastName?.trim() || host.lastName?.trim() || '';
  return `${firstName.charAt(0)} ${lastName.charAt(0)}`.toUpperCase();
};

export const formatHosts = (hosts: HostInput[] = []): HostInfo[] => {
  return hosts
    .filter((host) => {
      const isValid = host.externalId && host.firstName && host.lastName;
      if (!isValid) {
        console.warn('Invalid host entry filtered out:', host);
      }
      return isValid;
    })
    .map((host) => ({
      id: host.externalId,
      name: generateHostInitials(host),
      src: host.avatar?.url ?? host.avatar?.thumbnailUrl ?? undefined,
      userType: host.type ?? 'provider',
    }));
};

export const normalizeSectionDetailsToFormState = (
  details: SectionData,
  timezone: string,
): GroupSectionFormData => {
  const startDt = DateTime.fromISO(details.startDate, { zone: 'utc' }).setZone(
    timezone,
  );
  const endDt = details.endDate
    ? DateTime.fromISO(details.endDate, { zone: 'utc' }).setZone(timezone)
    : null;

  const shouldDisableDayOfWeek = DAY_OF_WEEK_DISABLED_FREQUENCIES.has(
    details.frequency,
  );

  return {
    id: details.id,
    hosts: details.hosts.map((host) =>
      groupClassHostToAutoCompleteOption(host as GroupClassHost),
    ),
    frequency: details.frequency,
    dayOfWeek: shouldDisableDayOfWeek ? '' : startDt.toFormat('cccc'),
    sessionDateTime: startDt.toJSDate(),
    timezone,
    duration: details.duration,
    length: details.sessionCode || '',
    startDate: startDt.toFormat('yyyy-MM-dd'),
    endDate: endDt ? endDt.toFormat('yyyy-MM-dd') : '',
    maximumAttendees: details.maximumAttendees || 0,
    mustAttendAll: details.mustAttendAll,
    isWebinar: details.isWebinar,
    startTime: startDt.toFormat('HH:mm:ss'),
    showDate: details.showDate,
    showMaximumAttendees: details.showMaximumAttendees,
  };
};

export const isFormValid = (
  formState: GroupSectionFormData,
  shouldDisableDayOfWeek: boolean,
  isLiveGroup = false,
  isEditMode: boolean = false,
): boolean => {
  const isDayOfWeekRequired =
    (!isLiveGroup || !isEditMode) && !shouldDisableDayOfWeek;

  const hasRequiredFields =
    formState.hosts.length > 0 &&
    !!formState.frequency &&
    (isDayOfWeekRequired ? !!formState.dayOfWeek : true) &&
    !!formState.sessionDateTime &&
    !!formState.startDate &&
    !!formState.startTime &&
    !!formState.timezone &&
    !!formState.duration;

  const hasNoErrors = !getMaximumAttendeesErrorMessage(
    formState.maximumAttendees,
    GROUP_SECTION_MAX_ATTENDEES_LIMIT,
  );

  return hasRequiredFields && hasNoErrors;
};

export const getFrequencyFieldUpdates = (
  value: string,
  prev: GroupSectionFormData,
): Partial<GroupSectionFormData> => {
  if (!DAY_OF_WEEK_DISABLED_FREQUENCIES.has(value)) {
    return {
      dayOfWeek: '',
      startDate: '',
      startTime: '',
      sessionDateTime: null,
    };
  }
  const rounded = roundToNext5Minutes(DateTime.now().setZone(prev.timezone));
  return {
    dayOfWeek: '',
    startDate: rounded.toFormat('yyyy-MM-dd'),
    startTime: rounded.toFormat('HH:mm:ss'),
    sessionDateTime: rounded.toJSDate(),
  };
};

export const getDayOfWeekFieldUpdates = (
  value: string,
  prev: GroupSectionFormData,
): Partial<GroupSectionFormData> => {
  const nextDate = getNextOccurrenceDate(value, new Date());
  let nextDt = DateTime.fromJSDate(nextDate).setZone(prev.timezone);
  const updates: Partial<GroupSectionFormData> = {};

  if (prev.startTime) {
    const [hours, minutes, seconds] = parseTimeString(prev.startTime);
    nextDt = nextDt.set({ hour: hours, minute: minutes, second: seconds });
  } else {
    nextDt = roundToNext5Minutes(nextDt);
    updates.startTime = nextDt.toFormat('HH:mm:ss');
  }

  return {
    ...updates,
    startDate: nextDt.toFormat('yyyy-MM-dd'),
    sessionDateTime: nextDt.toJSDate(),
  };
};

interface FormatGroupSectionDateProps {
  date: string;
  timeZone: string;
}

export const formatGroupSectionDate = ({
  date,
  timeZone,
}: FormatGroupSectionDateProps): string => {
  const localDate = DateTime.fromISO(date, { zone: timeZone });

  if (!localDate.isValid) {
    return null;
  }

  return localDate.toFormat(DateFormat.weekdayMonthDate); // Monday, October 01
};

type GroupEnumerationEnrichmentInput = {
  ageRanges?: string[];
  attendeeRoles?: string[];
  groupStatusCode?: string;
  groupTypeCode?: string;
  treatmentModalities?: string[];
  treatmentPrograms?: string[];
};

export const enrichGroupWithEnumerations = (
  item: GroupEnumerationEnrichmentInput,
  programs: Enumeration[],
  modalities: TreatmentModalityEnumeration[],
  groupTypes: GroupTypeResponse[],
) => ({
  treatmentPrograms:
    item.treatmentPrograms?.map(
      (code) => programs.find((p) => p.code === code)?.label ?? code,
    ) ?? [],
  treatmentModalities:
    item.treatmentModalities?.map(
      (code) => modalities.find((m) => m.code === code)?.label ?? code,
    ) ?? [],
  attendeeRoles:
    item.attendeeRoles?.map((role) =>
      capitalizeFirstLetter(String(role).toLowerCase()),
    ) ?? [],
  ageRanges: formattedAgeRange(item.ageRanges),
  groupType:
    groupTypes.find((gt) => gt.code === item.groupTypeCode)?.type ??
    item.groupTypeCode,
  groupTypeCode:
    groupTypes.find((gt) => gt.code === item.groupTypeCode)?.code ??
    item.groupTypeCode,
  groupStatusCode: item.groupStatusCode,
});

/**
 * Enriches raw GroupList items with enumeration data (programs, modalities, group types)
 * Maps codes to display labels for treatmentPrograms, treatmentModalities, attendeeRoles, and ageRanges
 */
export const enrichGroupListWithEnumerations = (
  rawList: GroupList[],
  programs: Enumeration[],
  modalities: TreatmentModalityEnumeration[],
  groupTypes: GroupTypeResponse[],
): EnrichedGroupList[] => {
  return rawList.map((item) => ({
    ...item,
    ...enrichGroupWithEnumerations(item, programs, modalities, groupTypes),
  }));
};

export const getFormattedDurationWeeks = (sessionCode?: string): string => {
  const weekValue = sessionCode?.split('_')?.[1];

  if (!weekValue) return '';

  const isSingular = Number(weekValue) === 1;

  return `${weekValue} session${isSingular ? '' : 's'}`;
};

export const formattedAgeRange = (ageRanges?: string[]) =>
  ageRanges?.map((range) => formatAgeRangeDisplay(range)) ?? [];

export const getFilteredRegistrants = (
  searchTerm: string,
  registrants?: GetRegistrantsV2Response[],
): GetRegistrantsV2Response[] => {
  if (!registrants) return [];

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return registrants;
  }

  return registrants.filter((registrant) => {
    const preferredFullName = getPreferredFullName(
      registrant.chosenName,
      registrant.firstName,
      registrant.lastName,
      false,
    ).toLowerCase();

    const legalFullName =
      `${registrant.firstName ?? ''} ${registrant.lastName ?? ''}`
        .trim()
        .toLowerCase();

    return (
      preferredFullName.includes(normalizedSearchTerm) ||
      legalFullName.includes(normalizedSearchTerm)
    );
  });
};

export const mapRegistrantToAttendee = (
  registrant: GetRegistrantsV2Response,
): GroupClassEventAttendee => ({
  attended: registrant.isAttended,
  chosenName: registrant.chosenName,
  externalId: registrant.externalId,
  firstName: registrant.firstName,
  lastName: registrant.lastName,
  supportPatientLinks: registrant.supportPatientLinks,
  type: registrant.type as ParticipantType,
});

/**
 * Splits the registrants of a group section event into the users still expected
 * to attend and the users who declined, mapped to the shape shared with the
 * legacy group class event endpoints.
 */
export const splitRegistrantsByDeclineStatus = (
  registrants?: GetRegistrantsV2Response[],
): {
  declinedUsers: GroupClassEventAttendee[];
  registeredUsers: GroupClassEventAttendee[];
} => ({
  declinedUsers:
    registrants
      ?.filter(({ isDeclined }) => isDeclined)
      .map(mapRegistrantToAttendee) ?? [],
  registeredUsers:
    registrants
      ?.filter(({ isDeclined }) => !isDeclined)
      .map(mapRegistrantToAttendee) ?? [],
});

/** Group section event hosts are always providers, but the endpoint omits `type`. */
export const mapGroupEventHostsToAttendees = (
  hosts?: GroupEventV2Host[],
): GroupClassEventAttendee[] =>
  hosts?.map((host) => ({ ...host, type: ParticipantTypes.PROVIDER })) ?? [];

export const validateStartDateTime = (
  startDate: string,
  startTime: string,
  timezone: string,
  isEditMode: boolean = false,
): string => {
  if (!startDate || !startTime) {
    return '';
  }

  if (isEditMode) {
    return '';
  }

  const selectedDateTime = DateTime.fromFormat(
    `${startDate} ${startTime}`,
    'yyyy-MM-dd HH:mm:ss',
    { zone: timezone },
  );

  const now = DateTime.now().setZone(timezone);

  if (selectedDateTime < now) {
    return 'Start datetime cannot be in the past';
  }

  return '';
};

export const validateDayOfWeekMatch = (
  startDate: string,
  dayOfWeek: string,
  timezone: string,
  shouldDisableDayOfWeek: boolean = false,
): string => {
  if (!startDate || !dayOfWeek || shouldDisableDayOfWeek) {
    return '';
  }

  const selectedDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd', {
    zone: timezone,
  });

  if (!selectedDate.isValid) {
    return '';
  }

  const actualDayOfWeek = selectedDate.toFormat('cccc');

  if (actualDayOfWeek !== dayOfWeek) {
    return `Selected date is a ${actualDayOfWeek}, but ${dayOfWeek} was expected`;
  }

  return '';
};

export const calculateStartDateUpdate = (
  startDate: string,
  startTime: string,
  timezone: string,
): Date | null => {
  if (!startDate || !startTime) {
    return null;
  }

  const [hours, minutes, seconds] = parseTimeString(startTime);
  const dt = DateTime.fromFormat(startDate, 'yyyy-MM-dd', {
    zone: timezone,
  }).set({ hour: hours, minute: minutes, second: seconds });

  return dt.toJSDate();
};

export const calculateTimezoneUpdate = (
  sessionDateTime: Date,
  newTimezone: string,
): {
  startDate: string;
  startTime: string;
  sessionDateTime: Date;
} => {
  const dt = DateTime.fromJSDate(sessionDateTime).setZone(newTimezone);
  return {
    startDate: dt.toFormat('yyyy-MM-dd'),
    startTime: dt.toFormat('HH:mm:ss'),
    sessionDateTime: dt.toJSDate(),
  };
};
